/*    
<summary>
  This functional component "Add Inventory Management Form" provides form to add new product qunatity details.
  Developer:Aashish Singh, Created Date:06-AAugust-2025
</summary>
<returns>Returns JSX</returns>
*/

import base64 from "base-64";
import { Field, Form, Formik } from "formik";
import { useState } from "react";
import { Button, Modal } from "react-bootstrap";
import { FormattedMessage } from "react-intl";
import { IOption } from "../../../models/ICommon";
import LoaderButton from "../../../shared-components/button/loader-button";
import FormikFormInput from "../../../shared-components/FormikFormHandlers/FormikFormInput";
import FormikFormInputForRegexCheck from "../../../shared-components/FormikFormHandlers/FormikFormInputForRegexCheck";
import FormikFormSelect from "../../../shared-components/FormikFormHandlers/FormikFormSelect";
import {
  AddHeadquarterValidationSchema,
  AddInventorySchema,
  EditHeadquarterValidationSchema,
} from "../../../shared-components/FormikFormHandlers/FormikValidationSchema";
import FormLoader from "../../../shared-components/FormLoader/FormLoader";
import { ShowHidePasswordButton } from "../../../shared-components/Grid/custom-grid-formatter";
import Label from "../../../shared-components/label/label";
import { formatMessage } from "../../../translations/format-message";
import addInventoryTypeEnum from "../../../constants/enums/add-inventory-type-enum";
import { integerRegex, integerRegexWithNegativeNum } from "../../../constants/constants";
import { toHalfWidth } from "../../../helpers/validate-helper";

interface IProps {
  addType: addInventoryTypeEnum;
  isLoading: boolean;
  submitHandler: (data: any) => void;
  onModalClosed: () => void;
  initialValues: any;
}

const AddInventoryInfoForm = (props: IProps) => {
  const { addType, isLoading, submitHandler, onModalClosed } = props;
  const getTitle = () => {
    switch (addType) {
      case addInventoryTypeEnum.Incoming:
        return "add_incoming_quantity";
      case addInventoryTypeEnum.AdjustedQuantity:
        return "add_adjusted_quantity";
      case addInventoryTypeEnum.InternalUse:
        return "add_internal_use_quantity";
      default:
        return "add_incoming_quantity";
    }
  };
  // console.log(props.initialValues)
  return (
    <>
      <Modal show={true} centered>
        <Modal.Header>
          <Modal.Title>
            {formatMessage(getTitle())}
            <button
              onClick={props.onModalClosed}
              disabled={props.isLoading}
              className="Crossicons"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="23"
                height="23"
                fill="currentColor"
                className="bi bi-x"
                viewBox="0 0 16 16"
              >
                <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z" />
              </svg>
            </button>
          </Modal.Title>
        </Modal.Header>
        <Formik
          initialValues={props.initialValues}
          enableReinitialize
          validateOnBlur={true}
          validateOnChange={true}
          validationSchema={AddInventorySchema}
          onSubmit={async (values) => {
            let adJustedQuantityInNumber = toHalfWidth(values.AdjustedQuantity);
            let incomingQuantityInNumber = toHalfWidth(values.IncomingQuantity);
            let internalUsedQuantityInNumber = toHalfWidth(values.InternalUsedQuantity);
            let adJustedQuantity = values.AddType === addInventoryTypeEnum.AdjustedQuantity
                ? (values.IsNegative === "1" ? parseInt(adJustedQuantityInNumber)*-1 : parseInt(adJustedQuantityInNumber))
                : 0;
            let notes =
              values.AddType === addInventoryTypeEnum.AdjustedQuantity
                ? values.Notes
                : null;
            let incomingQuantity =
              values.AddType === addInventoryTypeEnum.Incoming
                ? parseInt(incomingQuantityInNumber)
                : 0;
            let internalUsedQuantity =
              values.AddType === addInventoryTypeEnum.InternalUse
                ? parseInt(internalUsedQuantityInNumber)
                : 0;
            let addObj = {
              IncomingQuantity: incomingQuantity,
              InternalUsedQuantity: internalUsedQuantity,
              AdjustedQuantity: adJustedQuantity,
              Notes: notes,
            };
            props.submitHandler(addObj);
          }}
        >
          {({ setFieldValue, values }) => (
            <Form className="user-form">
              {props.isLoading && <FormLoader loading={props.isLoading} />}
              <Modal.Body>
                <div className="form-group mb-3 row">
                  <div className="col-12 mb-3">
                    <Label
                      className="label-style"
                      required={true}
                      label="product_name"
                    />
                    <Field
                      className="form-control"
                      name="ProductName"
                      type="text"
                      placeholder="product_name"
                      as={FormikFormInput}
                      disabled={true}
                    />
                  </div>
                  <div className="col-12 mb-3">
                    <Label
                      className="label-style"
                      required={true}
                      label="bar_code"
                    />
                    <Field
                      className="form-control"
                      name="Barcode"
                      type="text"
                      // placeholder="enter_bar_code"
                      as={FormikFormInput}
                      disabled={true}
                    />
                  </div>
                  {addType === addInventoryTypeEnum.Incoming && (
                    <div className="col-12 mb-3">
                      <Label
                        className="label-style"
                        required={true}
                        label="quantity"
                      />
                      <Field
                        name="IncomingQuantity"
                        className="form-control"
                        type="text"
                        placeholder="enter_quantity"
                        regex={integerRegex}
                        as={FormikFormInputForRegexCheck}
                      />
                    </div>
                  )}
                  {addType === addInventoryTypeEnum.InternalUse && (
                    <div className="col-12 mb-3">
                      <Label
                        className="label-style"
                        required={true}
                        label="quantity"
                      />
                      <Field
                        name="InternalUsedQuantity"
                        className="form-control"
                        type="text"
                        placeholder="enter_quantity"
                        regex={integerRegex}
                        as={FormikFormInputForRegexCheck}
                      />
                    </div>
                  )}
                  {addType === addInventoryTypeEnum.AdjustedQuantity && (
                    <>
                    <div className="row align-items-center mb-3">
                      <div className="col-md-3">
                        <Label
                          className="label-style mb-0"
                          required={true}
                          label="sign_quantity"
                        />
                      </div>
                      <div className="col-md-9 d-flex gap-4">
                        <div className="form-check form-check-inline">
                          <Field
                            className="form-check-input"
                            name="IsNegative"
                            type="radio"
                            value="0"
                            id="positive"
                          />
                          <label className="form-check-label" htmlFor="positive">
                            <i className="bi bi-plus"></i>
                          </label>
                        </div>
                        <div className="form-check form-check-inline">
                          <Field
                            className="form-check-input"
                            name="IsNegative"
                            type="radio"
                            value="1"
                            id="negative"
                          />
                          <label className="form-check-label" htmlFor="negative">
                            <i className="bi bi-dash"></i>
                          </label>
                        </div>
                      </div>
                    </div>
                      <div className="col-12 mb-3">
                        <Label
                          className="label-style"
                          required={true}
                          label="quantity"
                        />
                        <Field
                          name="AdjustedQuantity"
                          className="form-control"
                          type="text"
                          placeholder="enter_quantity"
                          regex={integerRegex}
                          as={FormikFormInputForRegexCheck}
                        />
                      </div>
                      <div className="col-12 mb-3">
                        <Label
                          className="label-style"
                          required={false}
                          label="note"
                        />
                        <Field
                          className="form-control"
                          name="Notes"
                          type="text"
                          placeholder="enter_note"
                          as={FormikFormInput}
                        />
                      </div>
                    </>
                  )}
                </div>
              </Modal.Body>
              {/* Footer Buttons */}
              <div className="modal-footer center-content">
                <Button
                  variant="outline-danger"
                  type="button"
                  onClick={props.onModalClosed}
                  disabled={props.isLoading}
                >
                  <i className="bi bi-x-lg me-1"></i>
                  <FormattedMessage id="close" />
                </Button>
                <LoaderButton
                  type="submit"
                  iconName="bi-check-lg"
                  isLoading={props.isLoading}
                  text="add"
                />
              </div>
            </Form>
          )}
        </Formik>
      </Modal>
    </>
  );
};

export default AddInventoryInfoForm;
